<script setup>
import { ref, computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import ContentVueEditor from '@components/ContentVueEditor.vue';
import { useAddressStore } from '@stores/address';
import { useTextCounter } from '@composables/useTextCounter';
import { trans, transchoice } from '@elements/translation';
import Logger from '@tools/logger/logger';
import { flashBagError, flashBagSuccess } from '@tools/flashbag';

const store = useAddressStore();
const {
  setCurrentPageSpaceValues, setInitialPageSpace, setCurrentPageSpace, updatePageSpace,
} = store;
const { initialPageSpace, currentPageSpace, completePageSpaceUrl } = storeToRefs(store);

const description = computed({
  get: () => currentPageSpace.value.description,
  set: (value) => {
    setCurrentPageSpaceValues({ description: value });
  },
});

const loading = ref(false);
const maxLength = 1500;

const { counter } = useTextCounter(description);

const hasError = computed(() => counter.value > maxLength);
const descriptionChanged = computed(() => description.value !== initialPageSpace.value.description);
const save = async () => {
  if (hasError.value || !descriptionChanged.value) {
    return;
  }

  try {
    loading.value = true;

    const data = await updatePageSpace(initialPageSpace.value, { description: description.value });

    if (!data) {
      throw new Error('Invalid response');
    }

    setInitialPageSpace(data);
    setCurrentPageSpace(data);

    flashBagSuccess(trans('addresses.details.form.success'));
  } catch (error) {
    Logger.error(error);
    flashBagError();
  }

  loading.value = false;
};

onMounted(() => {
  window.onbeforeunload = () => (descriptionChanged.value ? trans('addresses.details.form.validation.unload_alert') : null);
});
</script>

<template>
  <div class="seller-brand-description">
    <h3>{{ trans('address.details.seller_brand.form.description.title') }}</h3>
    <!-- eslint-disable vue/no-v-html -->
    <p
      class="seller-brand-description__subtitle"
      v-html="transchoice('address.details.seller_brand.form.description.subtitle', { link: completePageSpaceUrl })"
    />
    <!-- eslint-enable vue/no-v-html -->
    <ContentVueEditor
      v-model="description"
      :max-counter="maxLength"
    />

    <button
      class="seller-brand-description__save btn btn--primary"
      :class="{
        disabled: hasError,
        'loading-btn': loading,
      }"
      :disabled="hasError"
      @click="save"
    >
      {{ trans('addresses.details.form.submit') }}
    </button>
  </div>
</template>
